import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IoArrowRedoOutline,
  IoArrowUndoOutline,
  IoClose,
  IoDiceOutline,
  IoTrashOutline
} from 'react-icons/io5'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { Button, ConfirmModal, ProcessingModal } from '@renderer/components'
import PrintPreview, { type PrintPreviewRef } from '@renderer/components/PrintPreview'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { useDesignLayout } from '@renderer/hooks'
import type { Sticker } from '@renderer/types/configState'
import {
  COUNTDOWN_PLACEHOLDER,
  isCountdownUrgent,
  useCountdown
} from '@renderer/utils/countdownUtils'
import { generateUUID } from '@renderer/utils/idUtils'
import { artShape } from '@renderer/lib/stickers/artShape'
import { setScale } from '@renderer/lib/stickers/layoutSet'
import { autoLayoutSignature, layoutAuto } from '@renderer/lib/stickers/autoLayout'
import { BRUSH_SIZE_DEFAULT, brushSizePct, type BrushType } from '@renderer/lib/stickers/brush'
import { resolveEditStickerOptions } from '@renderer/lib/stickers/editOptions'
import type { StickerPlacement } from '@renderer/lib/stickers/bakeStickers'
import { useStickerGestures } from '@renderer/lib/stickers/useStickerGestures'
import { resolveEditStickerAvailability } from '../editStickerAvailability'
import { useSharedCountdown } from '../hooks/useSharedCountdown'
import { initialPlacements, placementsReducer } from './placements'
import { useFinishEdit } from './useFinishEdit'
import DrawLayer from './DrawLayer'
import BrushPanel, { BRUSH_COLORS } from './BrushPanel'

type Tab = 'stickers' | 'draw'

/** A sticker placed by hand, before the guest moves or pinches it. */
const SINGLE_WIDTH_PCT = 24
/** How long Continue waits for a just-tapped layout to measure its art. */
const LAYOUT_WAIT_MS = 3000
/** Shuffle draws again this many times at most to find a visibly new layout. */
const SHUFFLE_TRIES = 8

/**
 * The Edit page: decorate the strip that is about to print.
 *
 * One page for every project. The strip fills the stage; the tray sits beside
 * it (below it, on a portrait booth). Tapping a sticker:
 *
 * - with **Auto layout** on lays copies of it over the strip for the guest
 *   (`layoutAuto`: about one per photograph, never on a face), replacing the
 *   stickers already there. Shuffle — or tapping it again — lays it out anew;
 * - with Auto layout off drops one copy mid-strip.
 *
 * Every sticker on the strip, laid out or placed, is the guest's to drag,
 * pinch, turn and remove. Because a new auto layout wipes the stickers, the
 * page asks first whenever that would throw away the guest's own hand work.
 *
 * The Studio decides what is offered (`resolveEditStickerOptions`): the Auto
 * layout toggle (it starts on) and the Draw tab, the classic editor's brush.
 *
 * Placements and strokes are percentages of the strip, the same numbers the
 * print bake draws, so what the guest sees is what prints. Continue hands off
 * to the shared print-upload-video flow (useFinishEdit).
 */
const StickerPage: React.FC = () => {
  const { t } = useTranslation()
  const {
    gallery,
    galleryKey,
    userPreferences,
    id: sessionId,
    logActivity
  } = useSessionStateStore()
  const { getFrame, frames, config: globalConfig, stickers, stickersLoaded } = useConfigStore()
  const { isPortrait } = useDesignLayout()
  const primary = globalConfig?.theme?.primaryColor || '#3B82F6'

  const settings = globalConfig?.pages.edit?.settings
  const options = resolveEditStickerOptions(settings)
  const editTimeout = settings?.editTimeout || 0
  const captureSettings = globalConfig?.pages.capture?.settings
  const hasSharedTimeout =
    (captureSettings?.mode || 'standard') === 'frame-based' &&
    (captureSettings?.captureTimeout || 0) > 0
  const { remainingTime: sharedRemaining, isExpired: sharedExpired } = useSharedCountdown()

  const frame = getFrame(userPreferences.frameId || frames[0]?._id) || null
  const frameConfig = useMemo(
    () => ({
      id: frame?._id || '',
      baseImage: frame?.thumbnail || '',
      drawParams: frame?.drawParams || {
        canvasWidth: 1200,
        canvasHeight: 1800,
        backgroundColor: '#FFFFFF',
        images: []
      }
    }),
    [frame]
  )

  const tray: Sticker[] = useMemo(() => {
    const ids = settings?.stickerIds ?? []
    const byId = new Map(stickers.map((s) => [s._id, s]))
    return ids.map((id) => byId.get(id)).filter((s): s is Sticker => Boolean(s))
  }, [stickers, settings?.stickerIds])
  // Decided from the sticker list alone, like the classic editor's skip.
  const noStickers =
    resolveEditStickerAvailability({
      stickerIds: settings?.stickerIds,
      availableStickerCount: tray.length,
      catalogLoaded: stickersLoaded
    }) === 'none'
  // Nothing to stick and nothing to draw with: the page has nothing to offer.
  const nothingToOffer = noStickers && !options.drawEnabled
  const showTabs = options.drawEnabled && !noStickers

  const [tab, setTab] = useState<Tab>(noStickers ? 'draw' : 'stickers')
  const [autoOn, setAutoOn] = useState(options.autoLayout)
  const [state, dispatch] = useReducer(placementsReducer, initialPlacements)
  const [activeSticker, setActiveSticker] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  /** A tap waiting on "replace your stickers?" — the sticker to lay out. */
  const [confirming, setConfirming] = useState<Sticker | null>(null)
  const [brushType, setBrushType] = useState<BrushType>('solid')
  const [brushColor, setBrushColor] = useState(BRUSH_COLORS[0])
  const [brushSize, setBrushSize] = useState(BRUSH_SIZE_DEFAULT)
  const saltRef = useRef(0)
  const tapRef = useRef(0)
  /** The layout on the strip, as `autoLayoutSignature` sees it. */
  const signatureRef = useRef('')
  /**
   * A layout still measuring its art. It resolves with what it placed, or null
   * when a later tap replaced it. Cleared once it lands.
   */
  const pendingLayoutRef = useRef<Promise<StickerPlacement[] | null> | null>(null)
  const placementsRef = useRef(state.placements)
  placementsRef.current = state.placements
  const strokesRef = useRef(state.strokes)
  strokesRef.current = state.strokes

  // ---- the strip -----------------------------------------------------------
  const previewRef = useRef<PrintPreviewRef>(null)
  const [stripUrl, setStripUrl] = useState<string>(gallery.stripUrl || '')
  const [stripRatio, setStripRatio] = useState<number>(
    frameConfig.drawParams.canvasWidth / frameConfig.drawParams.canvasHeight
  )
  const [isGeneratingStrip, setIsGeneratingStrip] = useState(!gallery.stripUrl)

  useEffect(() => {
    if (gallery.stripUrl) {
      setStripUrl(gallery.stripUrl)
      setIsGeneratingStrip(false)
      return
    }
    if (stripUrl) return
    // Select normally leaves the plain strip in the gallery. If it did not,
    // render it here, as the original editor did.
    let alive = true
    void (async (): Promise<void> => {
      try {
        await Promise.race([
          previewRef.current?.waitForReady(),
          new Promise((resolve) => setTimeout(resolve, 5000))
        ])
        const blob = await previewRef.current?.exportImage()
        if (alive && blob) setStripUrl(URL.createObjectURL(blob))
      } catch (error) {
        console.error('[Stickers] strip generation failed:', error)
      } finally {
        if (alive) setIsGeneratingStrip(false)
      }
    })()
    return (): void => {
      alive = false
    }
  }, [gallery.stripUrl])

  // ---- the stage: the strip at its own shape, as large as fits ----------------
  const stageRef = useRef<HTMLDivElement>(null)
  const stripRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const fit = (): void => {
      // The content box: the stage's padding is breathing room, not strip.
      const cs = getComputedStyle(el)
      const width = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)
      const height = el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom)
      const w = Math.max(0, Math.min(width, height * stripRatio))
      setBox({ width: w, height: w / stripRatio })
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    return (): void => observer.disconnect()
  }, [stripRatio])

  // ---- Auto layout -----------------------------------------------------------
  /**
   * Lay `sticker` out over the strip. Each call draws a new seed, and keeps
   * drawing (a few times at most) until the arrangement differs from the one
   * on screen, so a shuffle always visibly changes the strip.
   */
  const layOut = useCallback(
    (sticker: Sticker): Promise<StickerPlacement[] | null> => {
      const tap = ++tapRef.current
      const pending = artShape(sticker.url).then(({ ratio, coverage }) => {
        if (tap !== tapRef.current) return null
        let stamps = layoutAuto(
          frameConfig.drawParams,
          ratio,
          `${sessionId}:${sticker._id}:${++saltRef.current}`,
          setScale(coverage)
        )
        for (
          let i = 0;
          i < SHUFFLE_TRIES && autoLayoutSignature(stamps) === signatureRef.current;
          i++
        ) {
          stamps = layoutAuto(
            frameConfig.drawParams,
            ratio,
            `${sessionId}:${sticker._id}:${++saltRef.current}`,
            setScale(coverage)
          )
        }
        signatureRef.current = autoLayoutSignature(stamps)
        const placed: StickerPlacement[] = stamps.map((s) => ({
          id: generateUUID(),
          stickerId: sticker._id,
          url: sticker.url,
          ...s
        }))
        dispatch({ type: 'replace', placements: placed })
        if (pendingLayoutRef.current === pending) pendingLayoutRef.current = null
        return placed
      })
      pendingLayoutRef.current = pending
      return pending
    },
    [frameConfig.drawParams, sessionId]
  )

  const runLayout = (sticker: Sticker, reason: 'chosen' | 'shuffled'): void => {
    setSelectedId(null)
    setActiveSticker(sticker._id)
    void layOut(sticker)
    logActivity(`Stickers: auto layout ${reason} (${sticker.name})`)
  }

  /** Lay out, asking first when that would wipe the guest's own hand work. */
  const requestLayout = (sticker: Sticker, reason: 'chosen' | 'shuffled'): void => {
    if (state.manual && state.placements.length > 0) {
      setConfirming(sticker)
      return
    }
    runLayout(sticker, reason)
  }

  const shuffle = (): void => {
    const sticker = tray.find((s) => s._id === activeSticker)
    if (sticker) requestLayout(sticker, 'shuffled')
  }

  // ---- placing by hand ---------------------------------------------------------
  const gestures = useStickerGestures(stripRef, {
    onSelect: setSelectedId,
    get: (id) => placementsRef.current.find((p) => p.id === id),
    move: (id, xPct, yPct) => dispatch({ type: 'move', id, xPct, yPct }),
    transform: (id, widthPct, rotation) => dispatch({ type: 'transform', id, widthPct, rotation }),
    onGestureStart: () => dispatch({ type: 'checkpoint' })
  })

  const addSingle = (sticker: Sticker): void => {
    const placementId = generateUUID()
    dispatch({
      type: 'add',
      placement: {
        id: placementId,
        stickerId: sticker._id,
        url: sticker.url,
        xPct: 50,
        yPct: 50,
        widthPct: SINGLE_WIDTH_PCT,
        rotation: 0
      }
    })
    setSelectedId(placementId)
    logActivity(`Stickers: added ${sticker.name}`)
  }

  const tapSticker = (sticker: Sticker): void => {
    if (autoOn) requestLayout(sticker, activeSticker === sticker._id ? 'shuffled' : 'chosen')
    else addSingle(sticker)
  }

  const clearAll = (): void => {
    tapRef.current++
    pendingLayoutRef.current = null
    signatureRef.current = ''
    setActiveSticker(null)
    setSelectedId(null)
    dispatch({ type: 'clear' })
    logActivity('Stickers: cleared')
  }

  // ---- Continue, the timer, and the nothing-to-offer case ----------------------
  const { finish, isProcessing } = useFinishEdit(previewRef)
  const continueNow = useCallback((): void => {
    // A layout tapped a moment ago may still be measuring its art: give it up to
    // LAYOUT_WAIT_MS to land, so the print carries what the strip shows.
    // The layout's own result is used: the ref only catches up on the next render.
    const pending = pendingLayoutRef.current
    const settle = pending
      ? Promise.race([pending, new Promise<null>((r) => setTimeout(() => r(null), LAYOUT_WAIT_MS))])
      : Promise.resolve(null)
    void settle.then((placed) =>
      finish(stripUrl, placed ?? placementsRef.current, strokesRef.current)
    )
  }, [finish, stripUrl])
  const continueRef = useRef(continueNow)
  continueRef.current = continueNow

  const countdown = useCountdown(editTimeout)
  const countdownRef = useRef(countdown)
  countdownRef.current = countdown
  const [remaining, setRemaining] = useState(COUNTDOWN_PLACEHOLDER)
  useEffect(() => {
    if (hasSharedTimeout || editTimeout <= 0) return
    const clock = countdownRef.current
    clock.setOnTick(() => setRemaining(clock.getFormattedRemainingSeconds()))
    clock.setOnComplete(() => {
      logActivity('Stickers: timeout, continuing')
      continueRef.current()
    })
    clock.start()
    return (): void => {
      clock.stop()
      clock.setOnComplete(null)
      clock.setOnTick(null)
    }
  }, [editTimeout, hasSharedTimeout, logActivity])

  useEffect(() => {
    if (hasSharedTimeout && sharedExpired && !isGeneratingStrip) {
      logActivity('Stickers: shared countdown expired, continuing')
      continueRef.current()
    }
  }, [hasSharedTimeout, sharedExpired, isGeneratingStrip, logActivity])

  // A project with the page on but nothing to offer still owes the guest a
  // print: Select left printing to this page. Finish straight away rather than
  // showing an empty page.
  useEffect(() => {
    if (nothingToOffer && !isGeneratingStrip) {
      logActivity('Stickers: none configured, finishing without stickers')
      continueRef.current()
    }
  }, [nothingToOffer, isGeneratingStrip, logActivity])

  const timerText = hasSharedTimeout ? sharedRemaining : editTimeout > 0 ? remaining : ''
  const hasContent = state.placements.length > 0 || state.strokes.length > 0
  const drawing = tab === 'draw'

  // ---- render ----------------------------------------------------------------
  const roundButton =
    'flex h-14 w-14 items-center justify-center rounded-full bg-white text-carbon shadow-md transition-opacity disabled:opacity-40'

  const stage = (
    <div
      ref={stageRef}
      className={`relative flex min-h-0 min-w-0 flex-1 items-center justify-center ${
        isPortrait ? 'px-24 pb-4 pt-2' : 'px-24 py-10'
      }`}
      onPointerDown={() => setSelectedId(null)}
    >
      <div className="relative" style={{ width: box.width, height: box.height }}>
        <div ref={stripRef} className="relative h-full w-full">
          {stripUrl && (
            <img
              src={stripUrl}
              alt=""
              crossOrigin={stripUrl.startsWith('http') ? 'anonymous' : undefined}
              onLoad={(e) => {
                const img = e.currentTarget
                if (img.naturalWidth && img.naturalHeight) {
                  setStripRatio(img.naturalWidth / img.naturalHeight)
                }
              }}
              className="h-full w-full rounded-md object-contain shadow-[0_24px_48px_-20px_rgba(15,23,42,0.35),0_4px_12px_rgba(15,23,42,0.08)]"
            />
          )}
          <DrawLayer
            width={box.width}
            height={box.height}
            strokes={state.strokes}
            active={drawing}
            brush={{ type: brushType, color: brushColor, sizePct: brushSizePct(brushSize) }}
            onStroke={(stroke) => dispatch({ type: 'stroke', stroke })}
          />
          {state.placements.map((p, i) => (
            <div
              key={p.id}
              className={`absolute ${drawing ? 'pointer-events-none' : ''}`}
              style={{
                left: `${p.xPct}%`,
                top: `${p.yPct}%`,
                width: `${p.widthPct}%`,
                transform: `translate(-50%, -50%) rotate(${p.rotation}deg)`
              }}
            >
              <img
                src={p.url}
                alt=""
                draggable={false}
                onPointerDown={(e): void => gestures.begin(e, p.id)}
                className="block w-full cursor-grab touch-none select-none drop-shadow-[0_3px_4px_rgba(0,0,0,0.25)]"
                style={
                  state.manual
                    ? undefined
                    : { animation: `sticker-pop 420ms cubic-bezier(.2,1.4,.4,1) ${i * 60}ms both` }
                }
              />
              {!drawing && selectedId === p.id && (
                <>
                  <div
                    className="pointer-events-none absolute -inset-2 rounded-lg border-2 border-dashed"
                    style={{ borderColor: primary }}
                  />
                  <button
                    type="button"
                    aria-label={t('stickers.remove', 'Remove sticker')}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      dispatch({ type: 'remove', id: p.id })
                      setSelectedId(null)
                    }}
                    className="absolute -right-5 -top-5 flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-white shadow-md"
                  >
                    <IoClose size={24} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
        {/* Undo and Clear ride beside the strip they act on. */}
        <div
          className="absolute left-full top-0 ml-5 flex flex-col gap-3"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-label={t('stickers.undo', 'Undo')}
            onClick={() => {
              dispatch({ type: 'undo' })
              setSelectedId(null)
            }}
            disabled={state.history.length === 0}
            className={roundButton}
          >
            <IoArrowUndoOutline size={26} />
          </button>
          <button
            type="button"
            aria-label={t('stickers.redo', 'Redo')}
            onClick={() => {
              dispatch({ type: 'redo' })
              setSelectedId(null)
            }}
            disabled={state.future.length === 0}
            className={roundButton}
          >
            <IoArrowRedoOutline size={26} />
          </button>
          <button
            type="button"
            aria-label={t('stickers.clear', 'Clear all')}
            onClick={clearAll}
            disabled={!hasContent}
            className={roundButton}
          >
            <IoTrashOutline size={24} />
          </button>
        </div>
      </div>
    </div>
  )

  const tabs = showTabs && (
    <div className="mb-5 flex rounded-full bg-slate-100 p-1">
      {(['stickers', 'draw'] as const).map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => {
            setSelectedId(null)
            setTab(key)
          }}
          aria-pressed={tab === key}
          className={`flex-1 rounded-full py-2.5 text-lg font-semibold transition-all ${
            tab === key ? 'bg-white text-carbon shadow' : 'text-slate-500'
          }`}
        >
          {key === 'stickers'
            ? t('stickers.tabStickers', 'Stickers')
            : t('stickers.tabDraw', 'Draw')}
        </button>
      ))}
    </div>
  )

  const autoRow = options.autoLayout && (
    <div className="mb-4 flex items-center justify-between gap-4">
      <span className="text-lg font-semibold text-carbon">
        {t('stickers.autoLayout', 'Auto layout')}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={autoOn}
        aria-label={t('stickers.autoLayout', 'Auto layout')}
        onClick={() => {
          setAutoOn(!autoOn)
          logActivity(`Stickers: auto layout ${autoOn ? 'off' : 'on'}`)
        }}
        className="relative h-9 w-16 shrink-0 rounded-full transition-colors"
        style={{ background: autoOn ? primary : '#cbd5e1' }}
      >
        <span
          className="absolute top-1 h-7 w-7 rounded-full bg-white shadow transition-all"
          style={{ left: autoOn ? 'calc(100% - 2rem)' : '0.25rem' }}
        />
      </button>
    </div>
  )

  const stickerTray = (
    <div
      className={`grid min-h-0 flex-1 content-start gap-3 overflow-y-auto p-1 ${
        isPortrait ? 'grid-cols-5' : 'grid-cols-3'
      }`}
    >
      {tray.map((sticker) => {
        const on = autoOn && activeSticker === sticker._id
        return (
          <button
            key={sticker._id}
            type="button"
            aria-label={sticker.name}
            aria-pressed={on}
            onClick={() => tapSticker(sticker)}
            className="flex aspect-square items-center justify-center rounded-2xl border-2 bg-slate-50 p-3 transition-all active:scale-95"
            style={
              on ? { borderColor: primary, background: `${primary}12` } : { borderColor: '#eef2f7' }
            }
          >
            <img
              src={sticker.thumbnail || sticker.url}
              alt=""
              crossOrigin="anonymous"
              className="max-h-full max-w-full object-contain"
            />
          </button>
        )
      })}
    </div>
  )

  const actions = (
    <div className="mt-5 flex items-center gap-3">
      {timerText && (
        <span
          className={`shrink-0 font-mono text-2xl tabular-nums ${
            isCountdownUrgent(timerText) ? 'font-bold text-red-600' : 'text-carbon'
          }`}
        >
          {timerText}
        </span>
      )}
      {!drawing && autoOn && activeSticker && (
        <Button
          variant="secondary"
          onClick={shuffle}
          text={
            <span className="flex items-center gap-2">
              <IoDiceOutline size={24} />
              {t('stickers.shuffle', 'Shuffle')}
            </span>
          }
        />
      )}
      <Button
        text={t('common.next', 'Next')}
        onClick={continueNow}
        disabled={isProcessing || isGeneratingStrip}
        className="flex-1"
        data-nav-to="user-reprint"
      />
    </div>
  )

  const panel = (
    <GlassPanel
      className={`flex min-h-0 flex-col ${
        isPortrait ? 'mx-6 mb-6 h-[40%] p-6' : 'my-8 mr-8 w-[34%] min-w-[400px] max-w-[600px] p-8'
      }`}
      style={{ background: 'rgba(255,255,255,0.94)' }}
    >
      {!isPortrait && (
        <h1 className="mb-5 text-3xl font-bold text-carbon">
          {t('stickers.pageTitle', 'Decorate your strip')}
        </h1>
      )}
      {tabs}
      {drawing ? (
        <BrushPanel
          type={brushType}
          color={brushColor}
          size={brushSize}
          primary={primary}
          onType={setBrushType}
          onColor={setBrushColor}
          onSize={setBrushSize}
        />
      ) : (
        <>
          {autoRow}
          {stickerTray}
        </>
      )}
      {actions}
    </GlassPanel>
  )

  return (
    <BackgroundContainer
      className={`relative flex h-full min-h-0 ${isPortrait ? 'flex-col' : 'flex-row'}`}
    >
      <style>{`@keyframes sticker-pop{0%{opacity:0;transform:scale(.3) rotate(-18deg)}70%{opacity:1;transform:scale(1.12)}100%{opacity:1;transform:scale(1)}}`}</style>
      {isPortrait && (
        <h1 className="px-10 pt-10 text-4xl font-bold text-carbon">
          {t('stickers.pageTitle', 'Decorate your strip')}
        </h1>
      )}
      {stage}
      {panel}

      <ConfirmModal
        isOpen={confirming !== null}
        title={t('stickers.replaceTitle', 'Replace all stickers?')}
        message={t(
          'stickers.replaceMessage',
          'The stickers you placed will be removed and laid out again automatically.'
        )}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          const sticker = confirming
          setConfirming(null)
          if (sticker) runLayout(sticker, activeSticker === sticker._id ? 'shuffled' : 'chosen')
        }}
      />
      <ProcessingModal
        isVisible={isProcessing}
        title={t('stickers.processing', 'Processing...')}
        message={t('stickers.processingMessage', 'Please wait while we finish your strip')}
        primaryColor={primary}
        opaque
      />
      <ProcessingModal
        isVisible={isGeneratingStrip}
        title={t('stickers.preparing', 'Preparing your strip...')}
        message=""
        primaryColor={primary}
        opaque
        cancelAfterSeconds={20}
        cancelLabel={t('edit.generatingStripSkip', 'Continue anyway')}
        onCancel={() => setIsGeneratingStrip(false)}
      />

      {/* Renders the strip when Select did not leave one (see above). */}
      <div className="hidden">
        <PrintPreview
          ref={previewRef}
          frameConfig={frameConfig}
          selectedPhotos={gallery.capturedItems
            .filter((item) => item.photoUrl)
            .map((item) => item.photoUrl)}
          isInteractive={false}
          sessionId={galleryKey || gallery.id}
        />
      </div>
    </BackgroundContainer>
  )
}

export default StickerPage
